import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { AssetRegistry } from '../systems/AssetRegistry';
import { PlayerPreferences, type ColorVisionMode, type TooltipDelay } from '../systems/PlayerPreferences';
import { TutorialManager } from '../systems/TutorialManager';
import { VisualTheme, addPanel } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';

interface SettingsSceneData { returnScene?: string; }
type SettingsTab = 'Accessibility' | 'Controls' | 'Gameplay' | 'Audio';

export class SettingsScene extends Phaser.Scene {
  private returnScene?: string;
  private tab: SettingsTab = 'Accessibility';

  constructor() {
    super(SceneKeys.Settings);
  }

  init(data: SettingsSceneData) {
    this.returnScene = data.returnScene;
  }

  create() {
    this.render();
    this.input.keyboard?.on('keydown-ESC', () => this.close());
  }

  private render() {
    this.children.removeAll();
    const { width, height } = this.scale;
    const prefs = PlayerPreferences.instance;
    const state = prefs.value;
    const background = AssetRegistry.instance.image('main_menu_background', 'title_screen');
    this.add.image(width / 2, height / 2, this.textures.exists(background) ? background : 'fallback.title').setDisplaySize(width, height).setAlpha(0.48);
    this.add.rectangle(width / 2, height / 2, width, height, VisualTheme.colors.ink, 0.78);
    addPanel(this, width / 2, height / 2, 1320, 760, { fill: state.highContrast ? 0x04070a : VisualTheme.colors.night, alpha: 0.985, stroke: VisualTheme.colors.goldBright });

    this.add.text(180, 94, 'Settings & Accessibility', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: `${prefs.fontSize(40)}px`,
      color: VisualTheme.text.title,
      stroke: '#100a06',
      strokeThickness: 3,
    });
    this.add.text(182, 146, 'Persistent across campaigns. Press F10 from any screen to return here.', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: `${prefs.fontSize(16)}px`,
      color: VisualTheme.text.accent,
    });

    const tabs: SettingsTab[] = ['Accessibility', 'Controls', 'Gameplay', 'Audio'];
    tabs.forEach((tab, index) => createTextButton(this, {
      label: tab,
      x: 310 + index * 320,
      y: 205,
      width: 270,
      height: 48,
      fontSize: 18,
      onClick: () => { this.tab = tab; this.render(); },
    }));

    this.add.rectangle(width / 2, 250, 1180, 1, VisualTheme.colors.goldBright, 0.3);
    if (this.tab === 'Accessibility') this.drawAccessibility();
    if (this.tab === 'Controls') this.drawControls();
    if (this.tab === 'Gameplay') this.drawGameplay();
    if (this.tab === 'Audio') this.drawAudio();

    createTextButton(this, { label: 'Reset Defaults', x: 330, y: 820, width: 250, onClick: () => { prefs.reset(); TutorialManager.instance.reset(); this.render(); } });
    createTextButton(this, { label: 'Guided Tutorial', x: 800, y: 820, width: 250, onClick: () => {
      TutorialManager.instance.reset();
      this.scene.pause(SceneKeys.Settings);
      this.scene.launch(SceneKeys.Tutorial, { returnScene: SceneKeys.Settings });
    } });
    createTextButton(this, { label: 'Done', x: 1270, y: 820, width: 250, onClick: () => this.close() });

    const exposed = window as unknown as { render_game_to_text?: () => string };
    exposed.render_game_to_text = () => JSON.stringify({ mode: 'settings', tab: this.tab, preferences: prefs.value });
  }

  private drawAccessibility() {
    const prefs = PlayerPreferences.instance;
    const state = prefs.value;
    this.row(310, 'Text size', `${Math.round(state.textScale * 100)}%`, () => prefs.update({ textScale: this.cycleNumber(state.textScale, [0.9, 1, 1.15, 1.3, 1.4]) }));
    this.row(390, 'Interface scale', `${Math.round(state.uiScale * 100)}%`, () => prefs.update({ uiScale: this.cycleNumber(state.uiScale, [0.9, 1, 1.1, 1.2]) }));
    this.row(470, 'High contrast', state.highContrast ? 'On' : 'Off', () => prefs.update({ highContrast: !state.highContrast }));
    this.row(550, 'Color-vision palette', this.label(state.colorVision), () => prefs.update({ colorVision: this.cycleValue(state.colorVision, ['standard', 'deuteranopia', 'protanopia', 'tritanopia'] as ColorVisionMode[]) }));
    this.row(630, 'Reduced motion', state.reducedMotion ? 'On' : 'Off', () => prefs.update({ reducedMotion: !state.reducedMotion }));
    this.row(710, 'Subtitles and written cues', state.subtitles ? 'On' : 'Off', () => prefs.update({ subtitles: !state.subtitles }));
    this.description('High contrast and color-vision modes add shape, luminance and label differences instead of relying on hue alone. Reduced motion removes button tweening and non-essential transitions.');
  }

  private drawControls() {
    const prefs = PlayerPreferences.instance;
    const state = prefs.value;
    this.row(310, 'Camera speed', `${state.cameraSpeed.toFixed(1)}×`, () => prefs.update({ cameraSpeed: this.cycleNumber(state.cameraSpeed, [0.6, 0.8, 1, 1.2, 1.5, 1.8]) }));
    this.row(390, 'Edge panning', state.edgePan ? 'On' : 'Off', () => prefs.update({ edgePan: !state.edgePan }));
    this.row(470, 'Screen shake', state.screenShake ? 'On' : 'Off', () => prefs.update({ screenShake: !state.screenShake }));
    this.add.text(260, 560, 'Keyboard', { fontFamily: VisualTheme.fonts.display, fontSize: '21px', color: VisualTheme.text.accent });
    this.add.text(260, 604, 'Tab / Shift+Tab: move focus   •   Enter / Space: activate   •   F1: guide   •   F10: settings\nWASD / arrows: camera or focus   •   1–9: map modes   •   Home: reset camera   •   F: fullscreen', {
      fontFamily: VisualTheme.fonts.body, fontSize: '18px', color: VisualTheme.text.body, lineSpacing: 8,
    });
    this.add.text(260, 690, 'Gamepad', { fontFamily: VisualTheme.fonts.display, fontSize: '21px', color: VisualTheme.text.accent });
    this.add.text(260, 734, 'D-pad / left stick: move focus   •   South button: activate   •   Start: pause   •   Select/View: guide', {
      fontFamily: VisualTheme.fonts.body, fontSize: '18px', color: VisualTheme.text.body,
    });
    this.description('Every standard button participates in the same keyboard and controller focus order. A bright double outline identifies the focused control.');
  }

  private drawGameplay() {
    const prefs = PlayerPreferences.instance;
    const state = prefs.value;
    this.row(310, 'Confirm end turn', state.confirmEndTurn ? 'On' : 'Off', () => prefs.update({ confirmEndTurn: !state.confirmEndTurn }));
    this.row(390, 'Auto-pause important events', state.autoPauseEvents ? 'On' : 'Off', () => prefs.update({ autoPauseEvents: !state.autoPauseEvents }));
    this.row(470, 'Guided tutorial on new campaigns', state.tutorialEnabled ? 'On' : 'Off', () => prefs.update({ tutorialEnabled: !state.tutorialEnabled }));
    this.row(550, 'Contextual hints', state.contextualHints ? 'On' : 'Off', () => prefs.update({ contextualHints: !state.contextualHints }));
    this.row(630, 'Tooltip delay', this.label(state.tooltipDelay), () => prefs.update({ tooltipDelay: this.cycleValue(state.tooltipDelay, ['instant', 'short', 'relaxed'] as TooltipDelay[]) }));
    this.description('The tutorial and contextual hints can be reopened with F1. Confirmation and event-pausing settings reduce accidental strategic commitments without changing game difficulty.');
  }

  private drawAudio() {
    const prefs = PlayerPreferences.instance;
    const state = prefs.value;
    this.row(310, 'Mute all sound', state.muted ? 'On' : 'Off', () => prefs.update({ muted: !state.muted }));
    this.row(390, 'Master volume', `${Math.round(state.masterVolume * 100)}%`, () => prefs.update({ masterVolume: this.cycleNumber(state.masterVolume, [0, 0.25, 0.5, 0.75, 1]) }));
    this.row(470, 'Music volume', `${Math.round(state.musicVolume * 100)}%`, () => prefs.update({ musicVolume: this.cycleNumber(state.musicVolume, [0, 0.25, 0.5, 0.75, 1]) }));
    this.row(550, 'Effects volume', `${Math.round(state.sfxVolume * 100)}%`, () => prefs.update({ sfxVolume: this.cycleNumber(state.sfxVolume, [0, 0.25, 0.5, 0.75, 1]) }));
    this.description('Written event descriptions, order summaries and tactical results remain available even when audio is muted.');
  }

  private row(y: number, name: string, value: string, onClick: () => void) {
    const prefs = PlayerPreferences.instance;
    this.add.text(260, y - 13, name, { fontFamily: VisualTheme.fonts.body, fontSize: `${prefs.fontSize(20)}px`, color: VisualTheme.text.body });
    createTextButton(this, { label: value, x: 1200, y, width: 280, height: 50, fontSize: 18, onClick: () => { onClick(); this.render(); } });
  }

  private description(text: string) {
    this.add.text(760, 770, text, { fontFamily: VisualTheme.fonts.body, fontSize: '14px', color: VisualTheme.text.muted, wordWrap: { width: 900 }, align: 'center' }).setOrigin(0.5);
  }

  private cycleNumber(current: number, values: number[]) {
    const index = values.findIndex((value) => Math.abs(value - current) < 0.01);
    return values[(Math.max(0, index) + 1) % values.length];
  }

  private cycleValue<T>(current: T, values: T[]) {
    const index = values.indexOf(current);
    return values[(Math.max(0, index) + 1) % values.length];
  }

  private label(value: string) {
    return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private close() {
    this.scene.stop(SceneKeys.Settings);
    if (this.returnScene && this.scene.isPaused(this.returnScene)) this.scene.resume(this.returnScene);
  }
}
